import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
/**
 * React runtime for `@barel/i18n`.
 *
 * - `I18nProvider` mounts a configured i18next + i18next-icu instance and
 *   pre-loads every namespace for the chosen locale before rendering its
 *   children. Suspense fallback is the consumer's responsibility (the
 *   provider returns its own children eagerly once the catalogues resolve).
 * - `useT(namespace?)` returns a typed `t(key, params?)` resolver bound to
 *   the active locale.
 * - `<Trans>` re-exports `react-i18next`'s `Trans` so consumers can splice
 *   nested elements into a translated message.
 * - `useFormatters()` exposes Intl helpers (date / time / number / list /
 *   relative-time / currency) bound to the active locale.
 *
 * This module is the canonical surface every web component should consume.
 * Storybook decorators in `@barel/ui` keep using the local stub until
 * the DS waves swap them out.
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import i18next from 'i18next';
import ICU from 'i18next-icu';
import { I18nextProvider, Trans as I18nextTrans, initReactI18next, useTranslation, } from 'react-i18next';
import { CATALOGUES, loadAllNamespaces } from './catalogues.js';
import { DEFAULT_NAMESPACE, FALLBACK_LOCALE, NAMESPACES, SUPPORTED_LOCALES, } from './locales.js';
import { pseudoLocaliseBundle } from './pseudo.js';
/**
 * Build a fresh, isolated i18next instance. Tests use this to avoid sharing
 * state between cases. App code should rely on `<I18nProvider>` instead.
 */
export function createI18nInstance(options) {
    const instance = i18next.createInstance();
    const baseResources = options.resources ?? CATALOGUES;
    const resources = options.pseudo ? pseudoLocaliseBundle(baseResources) : baseResources;
    // i18next does its own type-erasure for resources so we cast safely.
    const i18nResources = resources;
    void instance
        .use(ICU)
        .use(initReactI18next)
        .init({
        lng: options.locale,
        fallbackLng: FALLBACK_LOCALE,
        supportedLngs: [...SUPPORTED_LOCALES],
        ns: [...NAMESPACES],
        defaultNS: DEFAULT_NAMESPACE,
        resources: i18nResources,
        interpolation: { escapeValue: false },
        returnNull: false,
        missingKeyHandler: (lngs, ns, key) => {
            if (process.env.NODE_ENV !== 'production') {
                console.warn(`[i18n] missing key "${key}" in ns "${ns}" for ${lngs.join(',')}`);
            }
        },
        saveMissing: process.env.NODE_ENV !== 'production',
        react: { useSuspense: false },
    });
    return instance;
}
const I18nContext = createContext(null);
/** Read the active locale (or throw if no provider is mounted). */
export function useLocale() {
    const ctx = useContext(I18nContext);
    if (!ctx) {
        throw new Error('useLocale must be used within an <I18nProvider>');
    }
    return ctx.locale;
}
/** Imperatively change the active locale. */
export function useSetLocale() {
    const ctx = useContext(I18nContext);
    if (!ctx) {
        throw new Error('useSetLocale must be used within an <I18nProvider>');
    }
    return ctx.setLocale;
}
/**
 * Mount the i18n runtime. Children render once the locale catalogues are
 * available; in tests + SSR the eager bundle resolves synchronously.
 */
export function I18nProvider({ children, locale: initialLocale, resources, pseudo = false, loadingFallback = null, }) {
    const [locale, setLocale] = useState(initialLocale ?? FALLBACK_LOCALE);
    const [ready, setReady] = useState(resources !== undefined);
    const instanceRef = useRef(null);
    if (instanceRef.current === null) {
        instanceRef.current = createI18nInstance({ locale, resources, pseudo });
    }
    const instance = instanceRef.current;
    // If the parent flips the `locale` prop *after* mount (e.g., a user-pref
    // sync), follow along. We track the previous prop value via a ref so we
    // only react to actual changes, never to the initial mount value.
    const initialLocaleRef = useRef(initialLocale);
    useEffect(() => {
        if (initialLocale === undefined)
            return;
        if (initialLocale === initialLocaleRef.current)
            return;
        initialLocaleRef.current = initialLocale;
        setLocale(initialLocale);
    }, [initialLocale]);
    // Switch i18next + lazy-load any missing catalogues whenever locale flips.
    useEffect(() => {
        let cancelled = false;
        setReady(false);
        void (async () => {
            if (resources === undefined) {
                const loaded = await loadAllNamespaces(locale);
                if (cancelled)
                    return;
                const localised = pseudo
                    ? pseudoLocaliseBundle({ [locale]: loaded })[locale]
                    : loaded;
                if (localised) {
                    for (const [ns, bundle] of Object.entries(localised)) {
                        instance.addResourceBundle(locale, ns, bundle, true, true);
                    }
                }
            }
            await instance.changeLanguage(locale);
            if (!cancelled) {
                setReady(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [instance, locale, pseudo, resources]);
    const value = useMemo(() => ({ locale, setLocale, pseudo }), [locale, pseudo]);
    if (!ready) {
        return _jsx(_Fragment, { children: loadingFallback });
    }
    return (_jsx(I18nContext.Provider, { value: value, children: _jsx(I18nextProvider, { i18n: instance, children: children }) }));
}
/**
 * Translate strings against the active locale.
 *
 * @example
 *   const t = useT('inbox');
 *   return <h1>{t('list.title')}</h1>;
 */
export function useT(namespace = DEFAULT_NAMESPACE) {
    const { t } = useTranslation(namespace);
    return (key, params) => {
        const result = t(key, params ?? {});
        return typeof result === 'string' ? result : String(result);
    };
}
export const Trans = I18nextTrans;
const DEFAULT_DATE_OPTIONS = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
};
const DEFAULT_TIME_OPTIONS = {
    hour: '2-digit',
    minute: '2-digit',
};
/**
 * Locale-aware Intl helpers. All formatters short-circuit to the active
 * locale so consumers don't have to thread it manually.
 */
export function useFormatters() {
    const locale = useLocale();
    return useMemo(() => ({
        formatDate: (value, options) => new Intl.DateTimeFormat(locale, options ?? DEFAULT_DATE_OPTIONS).format(value),
        formatTime: (value, options) => new Intl.DateTimeFormat(locale, options ?? DEFAULT_TIME_OPTIONS).format(value),
        formatNumber: (value, options) => new Intl.NumberFormat(locale, options).format(value),
        formatCurrency: (value, currency, options) => new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
            ...(options ?? {}),
        }).format(value),
        formatRelative: (value, unit, options) => new Intl.RelativeTimeFormat(locale, options ?? { numeric: 'auto' }).format(value, unit),
        formatList: (values, options) => new Intl.ListFormat(locale, options ?? { style: 'long', type: 'conjunction' }).format(values),
    }), [locale]);
}
//# sourceMappingURL=runtime.js.map